import React from 'react';
import { type LucideIcon } from 'lucide-react';

type Props = {
  title: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  backgroundImageUrl?: string;
  backgroundPosition?: string;
  variant?: 'default' | 'glossy';
};

const HubCard: React.FC<Props> = ({
  title,
  icon: Icon,
  onClick,
  disabled = false,
  backgroundImageUrl,
  backgroundPosition,
  variant = 'default',
}) => {
  const isGlossy = variant === 'glossy' && !backgroundImageUrl;
  const cardClass = backgroundImageUrl
    ? 'border-slate-800/10'
    : isGlossy
      ? 'border-[#9adddb] bg-[linear-gradient(180deg,rgba(255,255,255,0.99)_0%,rgba(249,255,255,0.98)_54%,rgba(235,250,249,0.98)_100%)] hover:border-[#72cfcb] shadow-[0_20px_45px_rgba(10,186,181,0.14)] hover:shadow-[0_24px_54px_rgba(10,186,181,0.2)]'
      : 'border-slate-200 bg-white hover:border-slate-300';
  const titleClass = backgroundImageUrl
    ? 'text-white'
    : isGlossy
      ? 'text-slate-950'
      : 'text-slate-950';
  const iconCardClass = backgroundImageUrl
    ? 'bg-white/18 text-white backdrop-blur-sm'
    : isGlossy
      ? 'border border-[#8bd8d5] bg-white/90 text-[#0ABAB5] shadow-[inset_0_1px_0_rgba(255,255,255,0.95)] backdrop-blur-sm'
      : 'bg-slate-100 text-slate-700';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group relative w-full overflow-hidden rounded-[30px] border px-5 py-6 text-left transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 ${cardClass}`}
      style={backgroundImageUrl ? {
        backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.62), rgba(15,23,42,0.34)), url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: backgroundPosition ?? 'center',
      } : undefined}
    >
      {backgroundImageUrl ? <div className="absolute inset-0 bg-black/10" aria-hidden="true" /> : null}
      {isGlossy ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-5 top-0 h-12 rounded-b-[26px] bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,255,255,0.24))]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(180deg,rgba(10,186,181,0),rgba(10,186,181,0.2))]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-8 top-8 h-24 w-24 rounded-full bg-[#8ee7e2]/30 blur-2xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-5 left-0 w-1.5 rounded-r-full bg-[linear-gradient(180deg,rgba(10,186,181,0.9),rgba(10,186,181,0.45))]"
          />
        </>
      ) : null}
      <div className="flex min-h-[112px] items-center justify-between gap-4">
        <div className={`relative min-w-0 text-[28px] font-semibold tracking-[-0.03em] ${titleClass}`}>
          {title}
        </div>

        <div className="relative flex items-center justify-center self-stretch">
          <div className={`rounded-[22px] p-3 ${iconCardClass}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </div>
    </button>
  );
};

export default HubCard;
